import type { ApiManagerRequest } from "@orni/sdk-typescript";
import type {
  ApiCliId,
  ApiConfigApplyResult,
  ApiConfigPreview,
  ApiCurrentCliConfig,
  ApiImportCurrentConfigResult,
  ApiManagerSnapshot,
  ApiProtocolDetection,
  ApiProviderCredential,
  ApiProviderRecord,
  ApiProviderTestResult,
  ApiProxySettings,
  ApiRequestLogPage,
  SaveApiCliSettingsInput,
  SaveApiProviderInput,
} from "./sdk/api-types";

export type CloudApiManagerRequester = (request: ApiManagerRequest) => Promise<unknown>;

/** Restricted API Manager client whose requests still execute on the target computer. */
export class CloudApiManagerClient {
  private readonly request: CloudApiManagerRequester;

  constructor(request: CloudApiManagerRequester) {
    this.request = request;
  }

  snapshot = () => this.object<ApiManagerSnapshot>(
    { method: "GET", path: "/v1/api-manager" },
    "API Manager snapshot",
  );

  saveProvider = (input: SaveApiProviderInput) => this.object<ApiProviderRecord>(
    { method: "POST", path: "/v1/api-manager/providers", body: input },
    "API provider",
  );

  async deleteProvider(providerId: string, expectedRevision?: number): Promise<void> {
    const suffix = expectedRevision === undefined
      ? ""
      : `?expectedRevision=${encodeURIComponent(String(expectedRevision))}`;
    await this.request({
      method: "DELETE",
      path: `/v1/api-manager/providers/${encodeURIComponent(providerId)}${suffix}`,
    });
  }

  async providerCredential(providerId: string): Promise<ApiProviderCredential> {
    const result = await this.object<Record<string, unknown>>(
      {
        method: "GET",
        path: `/v1/api-manager/providers/${encodeURIComponent(providerId)}`,
      },
      "API provider credential",
    );
    // A Cloud client never exposes a target computer's stored credential,
    // even if an older Daemon returns the legacy apiKey field.
    return {
      providerId: typeof result.providerId === "string" ? result.providerId : providerId,
      ...(typeof result.maskedApiKey === "string" ? { maskedApiKey: result.maskedApiKey } : {}),
    };
  }

  testProviderEndpoint = (providerId: string) => this.object<ApiProviderTestResult>(
    {
      method: "POST",
      path: `/v1/api-manager/providers/${encodeURIComponent(providerId)}/test-endpoint`,
    },
    "API provider endpoint test",
  );

  testProviderModel = (providerId: string, model: string) => this.object<ApiProviderTestResult>(
    {
      method: "POST",
      path: `/v1/api-manager/providers/${encodeURIComponent(providerId)}/test-model`,
      body: { model },
    },
    "API provider model test",
  );

  async fetchModels(providerId: string): Promise<string[]> {
    const result = await this.object<Record<string, unknown>>(
      {
        method: "POST",
        path: `/v1/api-manager/providers/${encodeURIComponent(providerId)}/models`,
      },
      "API provider models",
    );
    if (!Array.isArray(result.models) || !result.models.every((model) => typeof model === "string")) {
      throw invalidResponse("API provider models");
    }
    return result.models;
  }

  detectProtocol = (input: { baseUrl: string; apiKey?: string }) =>
    this.object<ApiProtocolDetection>(
      { method: "POST", path: "/v1/api-manager/protocol/detect", body: input },
      "API protocol detection",
    );

  async updateProxy(input: { enabled: boolean; port: number; expectedRevision?: number }): Promise<ApiProxySettings> {
    const result = await this.object<ApiProxySettings & { apiKey?: unknown; api_key?: unknown }>(
      { method: "POST", path: "/v1/api-manager/proxy", body: input },
      "API proxy settings",
    );
    const { apiKey: _apiKey, api_key: _apiKeySnake, ...safe } = result;
    return safe;
  }

  async saveCliSettings(cli: ApiCliId, input: SaveApiCliSettingsInput): Promise<void> {
    await this.request({
      method: "POST",
      path: `/v1/api-manager/cli/${encodeURIComponent(cli)}`,
      body: input,
    });
  }

  async recoverCliBinding(cli: ApiCliId, bindingId: string): Promise<void> {
    await this.request({
      method: "POST",
      path: `/v1/api-manager/cli/${encodeURIComponent(cli)}/bindings/${encodeURIComponent(bindingId)}/recover`,
    });
  }

  currentConfig = (cli: ApiCliId) => this.object<ApiCurrentCliConfig>(
    { method: "GET", path: `/v1/api-manager/cli/${encodeURIComponent(cli)}/current` },
    "current CLI configuration",
  );

  importCurrentConfig = (cli: ApiCliId) => this.object<ApiImportCurrentConfigResult>(
    { method: "POST", path: `/v1/api-manager/cli/${encodeURIComponent(cli)}/import-current` },
    "imported CLI configuration",
  );

  async configPreview(cli: ApiCliId): Promise<ApiConfigPreview> {
    const result = await this.object<ApiConfigPreview>(
      { method: "GET", path: `/v1/api-manager/cli/${encodeURIComponent(cli)}/preview` },
      "CLI configuration preview",
    );
    if (result.contentRedacted) return result;
    // Rolling upgrades may briefly route to an older target Daemon. Keep the
    // Cloud UI from rendering file contents until the redaction flag exists.
    return {
      ...result,
      before: "[redacted on remote clients]",
      after: "[redacted on remote clients]",
      contentRedacted: true,
    };
  }

  applyConfig = (cli: ApiCliId, expectedSha256: string) => this.object<ApiConfigApplyResult>(
    {
      method: "POST",
      path: `/v1/api-manager/cli/${encodeURIComponent(cli)}/apply`,
      body: { expectedSha256 },
    },
    "applied CLI configuration",
  );

  logs = (filters: { cli?: ApiCliId; providerId?: string; protocolId?: string } = {}) => {
    const query = new URLSearchParams();
    if (filters.cli) query.set("cli", filters.cli);
    if (filters.providerId) query.set("providerId", filters.providerId);
    if (filters.protocolId) query.set("protocolId", filters.protocolId);
    return this.object<ApiRequestLogPage>(
      { method: "GET", path: `/v1/api-manager/logs${query.size ? `?${query}` : ""}` },
      "API request logs",
    );
  };

  private async object<T>(request: ApiManagerRequest, label: string): Promise<T> {
    const value = await this.request(request);
    if (!value || typeof value !== "object" || Array.isArray(value)) {
      throw invalidResponse(label);
    }
    return value as T;
  }
}

function invalidResponse(label: string): Error {
  return new TypeError(`The target computer returned an invalid ${label} response.`);
}
